import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShoppingCart, Search, Clock, Package, FileText,
  Activity, ArrowRight, RefreshCw, Star,
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import UserLayout from './UserLayout';
import CustomerBackorderWidget from './CustomerBackorderWidget';
import OrderStatsWidget from '../OrderStatsWidget';
import { showError } from '../../utils/notificationHandler';
import { useInquiryLists } from '../../hooks/useInquiryLists';
import { orderService } from '../../services/orderService';
import { Skeleton, SkeletonText } from '../shared/Skeleton';
import Card from '../shared/Card';
import Button from '../shared/Button';
import StatusBadge, { orderStatusTone, inquiryStatusTone } from '../shared/StatusBadge';
import QuickOrderBox from './QuickOrderBox';

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const getTimeAgo = (dateString) => {
  const mins = Math.floor((Date.now() - new Date(dateString)) / 60000);
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};

const INQUIRY_STATUS_LABELS = { draft: 'Draft', submitted: 'Submitted', quoted: 'Quoted' };

const isOpenOrder = (o) => !['Complete', 'Cancelled', 'c', 'C', 'x', 'X'].includes(o.status);

// Progress ERP stores single-letter codes ('2', 'c', 'h', 'x'); never show those raw to customers.
const formatOrderStatus = (order) => {
  if (order.internal_status === 'pending_approval') return 'Awaiting Approval';
  const s = order.status || '';
  const mapped = orderService.getStatusDisplayName(s);
  if (mapped !== s) return mapped;
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || 'Pending';
};

const UserDashboardNew = () => {
  const history = useHistory();
  const [userData, setUserData] = useState({});
  const [userId, setUserId] = useState(null);
  const [metrics, setMetrics] = useState({ cartItems: 0, cartValue: 0, pendingOrders: 0, openOrders: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [activityTab, setActivityTab] = useState('orders');
  const [systemStatus, setSystemStatus] = useState({ ok: true, checked: false });
  const [loading, setLoading] = useState(true);
  // Bumped by Refresh so the self-fetching widgets reload along with the page data.
  const [refreshKey, setRefreshKey] = useState(0);
  const statusIntervalRef = useRef(null);

  const { inquiryLists, loading: inquiriesLoading, refreshLists } = useInquiryLists(userId);
  const activeInquiries = inquiryLists
    .filter((l) => ['draft', 'submitted', 'quoted'].includes(l.status))
    .slice(0, 5);

  useEffect(() => {
    fetchDashboardData();
    checkSystemStatus();
    statusIntervalRef.current = setInterval(checkSystemStatus, 60000);
    return () => clearInterval(statusIntervalRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchUserData(),
        fetchCartMetrics(),
        fetchOrderMetrics(),
        fetchRecentActivity(),
        fetchFavorites(),
      ]);
    } catch (error) {
      showError(error, 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
    refreshLists();
    fetchDashboardData();
  };

  const fetchUserData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    setUserId(user.id);
    const { data } = await supabase
      .from('accounts')
      .select('company_name, full_name, is_admin, is_staff')
      .eq('id', user.id)
      .single();
    if (data) setUserData(data);
  };

  const fetchCartMetrics = () => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const cartValue = cart.reduce(
      (sum, item) => sum + parseFloat(item.price || 0) * (item.quantity || 1),
      0
    );
    setMetrics((prev) => ({ ...prev, cartItems: cart.length, cartValue }));
  };

  const fetchOrderMetrics = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data: orders } = await supabase
      .from('orders')
      .select('status, internal_status')
      .eq('user_id', user.id);
    if (!orders) return;
    const pending = orders.filter((o) => orderService.isAwaitingApproval(o)).length;
    const open = orders.filter(isOpenOrder).length;
    setMetrics((prev) => ({ ...prev, pendingOrders: pending, openOrders: open }));
  };

  const fetchRecentActivity = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const [{ data: ordersData }, { data: searchData }] = await Promise.all([
      supabase
        .from('orders')
        .select('order_number, status, internal_status, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(8),
      supabase
        .from('search_history')
        .select('part_number, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(8),
    ]);
    setRecentOrders(ordersData || []);
    setRecentSearches(searchData || []);
  };

  const fetchFavorites = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase
      .from('favorites')
      .select('part_number, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(6);
    setFavorites(data || []);
  };

  // The badge endpoint returns 200 whether the monitor is up or down, so read the
  // SVG's aria-label ("Status: Up") rather than res.ok.
  const checkSystemStatus = async () => {
    try {
      const res = await fetch('https://status.mhsonline.au/api/badge/1/status', { cache: 'no-cache' });
      if (!res.ok) throw new Error(`Status badge ${res.status}`);
      const svg = await res.text();
      setSystemStatus({ ok: svg.includes('aria-label="Status: Up"'), checked: true });
    } catch {
      // An unreachable status page says nothing about the portal itself, so hide the indicator.
      setSystemStatus({ ok: true, checked: false });
    }
  };

  // Greet the person, not the company; the company sits underneath.
  const firstName = (userData.full_name || '').trim().split(/\s+/)[0];

  const quotesReady = inquiryLists.filter((l) => l.status === 'quoted').length;

  // Neutral cards; colour is reserved for status and for "needs your action".
  const statCards = [
    {
      label: 'Quotes Ready',
      value: quotesReady,
      sub: quotesReady > 0 ? 'Review and add to cart' : 'No quotes waiting',
      icon: FileText,
      path: '/user/inquiry-lists',
      needsAction: quotesReady > 0,
      loading: !userId || inquiriesLoading,
    },
    {
      label: 'Awaiting Approval',
      value: metrics.pendingOrders,
      sub: 'Orders with MHS staff',
      icon: Clock,
      path: '/user/recent-orders?status=Awaiting%20Approval',
    },
    {
      label: 'Open Orders',
      value: metrics.openOrders,
      sub: 'Not yet complete',
      icon: Package,
      path: '/user/recent-orders',
    },
    {
      label: 'Cart',
      value: orderService.formatCurrency(metrics.cartValue),
      sub: `${metrics.cartItems} ${metrics.cartItems === 1 ? 'item' : 'items'}`,
      icon: ShoppingCart,
      path: '/user/cart',
    },
  ];

  const sectionLink = 'text-sm font-medium text-gray-600 hover:text-gray-900 flex items-center gap-1 transition-colors';
  const rowButton = 'w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-gray-50 transition-colors text-left group';

  const sectionHeader = (Icon, title, action) => (
    <div className="flex items-center justify-between">
      <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
        <Icon className="w-4 h-4 text-gray-500" aria-hidden="true" />
        {title}
      </h2>
      {action}
    </div>
  );

  return (
    <UserLayout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="space-y-6"
      >

        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {getGreeting()}
              {firstName && (
                <>
                  ,{' '}
                  {/* Brand yellow fails contrast as text on gray-50, so it's an underline accent instead */}
                  <span className="underline decoration-brand decoration-4 underline-offset-8">{firstName}</span>
                </>
              )}
            </h1>
            {userData.company_name && (
              <p className="text-sm text-gray-600 mt-2">{userData.company_name}</p>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1">
            {systemStatus.checked && (
              <a
                href="https://status.mhsonline.au/status/partnerportal"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                <span
                  className={`w-2 h-2 rounded-full ${
                    systemStatus.ok ? 'bg-green-500' : 'bg-orange-500'
                  }`}
                />
                {systemStatus.ok ? 'All systems operational' : 'Service degraded'}
              </a>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              icon={<RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />}
            >
              Refresh
            </Button>
          </div>
        </div>

        <QuickOrderBox userId={userId} />

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            const isLoading = stat.loading ?? loading;
            return (
              <button
                key={stat.label}
                onClick={() => history.push(stat.path)}
                className={`text-left bg-white p-5 rounded-lg shadow-sm border transition-shadow duration-200 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand group ${
                  stat.needsAction ? 'border-brand ring-1 ring-brand' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                  <span
                    className={`w-8 h-8 rounded-md flex items-center justify-center ${
                      stat.needsAction ? 'bg-brand text-brand-dark' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    <Icon className="w-4 h-4" aria-hidden="true" />
                  </span>
                </div>
                {isLoading ? (
                  <Skeleton className="h-8 w-20 mt-2" />
                ) : (
                  <p className="text-2xl font-bold text-gray-900 mt-2 tabular-nums">{stat.value}</p>
                )}
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  {stat.sub}
                  <ArrowRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" aria-hidden="true" />
                </p>
              </button>
            );
          })}
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left Column */}
          <div className="space-y-6">
            <Card
              padding="sm"
              header={sectionHeader(Star, 'Favourites')}
            >
              {loading ? (
                <SkeletonText lines={3} />
              ) : favorites.length > 0 ? (
                <div className="space-y-0.5">
                  {favorites.map((fav) => (
                    <button
                      key={fav.part_number}
                      onClick={() =>
                        history.push(
                          `/user/part-enquiry?partNumber=${encodeURIComponent(fav.part_number)}`
                        )
                      }
                      className={rowButton}
                    >
                      <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400 flex-shrink-0" aria-hidden="true" />
                      <span className="flex-1 text-sm font-mono font-medium text-gray-800 truncate">
                        {fav.part_number}
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Star className="w-8 h-8 text-gray-300 mx-auto mb-2" aria-hidden="true" />
                  <p className="text-sm text-gray-600">No favourites yet</p>
                  <p className="text-xs text-gray-500 mt-1">Star parts on the Part Enquiry page</p>
                </div>
              )}
            </Card>

            <Card
              padding="sm"
              header={sectionHeader(FileText, 'Inquiry Lists',
                <button onClick={() => history.push('/user/inquiry-lists')} className={sectionLink}>
                  View all <ArrowRight className="w-3 h-3" />
                </button>
              )}
            >
              {!userId || inquiriesLoading ? (
                <SkeletonText lines={3} />
              ) : activeInquiries.length > 0 ? (
                <div className="space-y-0.5">
                  {activeInquiries.map((inquiry) => {
                    return (
                      <button
                        key={inquiry.id}
                        onClick={() => history.push(`/user/inquiry-list/${inquiry.id}`)}
                        className={rowButton}
                      >
                        <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                        <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 truncate">
                          {inquiry.list_name}
                        </span>
                        <span className="text-xs text-gray-500 flex-shrink-0">{inquiry.item_count} items</span>
                        <StatusBadge tone={inquiryStatusTone(inquiry.status)}>
                          {INQUIRY_STATUS_LABELS[inquiry.status] || 'Draft'}
                        </StatusBadge>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6">
                  <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" aria-hidden="true" />
                  <p className="text-sm text-gray-600 mb-4">No active inquiry lists</p>
                  <Button onClick={() => history.push('/user/inquiry-lists')}>
                    Create Inquiry List
                  </Button>
                </div>
              )}
            </Card>
          </div>

          {/* Right Column (2/3) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Backorders first: it only renders when there's something outstanding */}
            {userId && <CustomerBackorderWidget userId={userId} refreshKey={refreshKey} />}

            <Card
              padding="sm"
              header={sectionHeader(Activity, 'Recent Activity',
                <div className="flex rounded-md border border-gray-200 overflow-hidden text-xs" role="tablist">
                  {[['orders', 'Orders'], ['searches', 'Searches']].map(([key, label], i) => (
                    <button
                      key={key}
                      role="tab"
                      aria-selected={activityTab === key}
                      onClick={() => setActivityTab(key)}
                      className={`px-3 py-1.5 font-medium transition-colors ${i > 0 ? 'border-l border-gray-200' : ''} ${
                        activityTab === key
                          ? 'bg-brand-dark text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            >
              {loading ? (
                <SkeletonText lines={4} />
              ) : activityTab === 'orders' ? (
                recentOrders.length > 0 ? (
                  <div className="space-y-0.5">
                    {recentOrders.map((order) => (
                      <button
                        key={order.order_number}
                        onClick={() =>
                          history.push(
                            `/user/recent-orders?search=${encodeURIComponent(order.order_number)}`
                          )
                        }
                        className={rowButton}
                      >
                        <Package className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                        <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 truncate">
                          {order.order_number}
                        </span>
                        <StatusBadge tone={orderStatusTone(order)}>{formatOrderStatus(order)}</StatusBadge>
                        <span className="w-14 text-right text-xs text-gray-500 flex-shrink-0">
                          {getTimeAgo(order.created_at)}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" aria-hidden="true" />
                    <p className="text-sm text-gray-600">No orders yet</p>
                  </div>
                )
              ) : recentSearches.length > 0 ? (
                <div className="space-y-0.5">
                  {recentSearches.map((s) => (
                    <button
                      key={`${s.part_number}-${s.created_at}`}
                      onClick={() =>
                        history.push(
                          `/user/part-enquiry?partNumber=${encodeURIComponent(s.part_number)}`
                        )
                      }
                      className={rowButton}
                    >
                      <Search className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                      <span className="flex-1 min-w-0 text-sm font-mono font-medium text-gray-900 truncate">
                        {s.part_number}
                      </span>
                      <span className="w-14 text-right text-xs text-gray-500 flex-shrink-0">
                        {getTimeAgo(s.created_at)}
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" aria-hidden="true" />
                  <p className="text-sm text-gray-600">No recent searches</p>
                </div>
              )}
            </Card>

            <OrderStatsWidget refreshKey={refreshKey} />
          </div>
        </div>
      </motion.div>
    </UserLayout>
  );
};

export default UserDashboardNew;
